import { calcTotalM3, calcTotalPcs, isGradoRw, parseNum, roundN } from './inventarioCalculos';

const has = (obj, field) => Object.prototype.hasOwnProperty.call(obj || {}, field);

/** M³.G de referencia cuando el campo viene vacío o en 0 pero la UI muestra otro total. */
function baseM3Ganancia(linea) {
  const g = parseNum(linea?.total_m3_ganancia);
  if (g != null && g > 0) return g;
  const m3 = parseNum(linea?.total_m3);
  if (m3 != null && m3 > 0) return m3;
  return parseNum(linea?.total_m3_original);
}

function m3Ganancia(linea) {
  const rw = isGradoRw(linea.grado);
  const alturaRw = rw ? parseNum(linea?.altura_rw) : null;
  const alturaGananciaRw =
    alturaRw != null && alturaRw > 0 ? alturaRw : null;
  return calcTotalM3({
    ...linea,
    espesor: linea.espesor_ganancia ?? linea.espesor,
    ancho: linea.ancho_ganancia ?? linea.ancho,
    largo: linea.largo_ganancia ?? linea.largo,
    castigo_rw: linea.castigo_rw_ganancia ?? linea.castigo_rw,
    altura_rw: rw ? alturaGananciaRw : linea.altura_rw,
  });
}

function ratioCampo(actual, anterior) {
  const nuevo = parseNum(actual);
  const previo = parseNum(anterior);
  if (nuevo == null || previo == null || previo === 0) return null;
  return nuevo / previo;
}

function m3Proporcional(anterior, actual, cambios, { ganancia = false } = {}) {
  const base = ganancia ? baseM3Ganancia(anterior) : parseNum(anterior.total_m3);
  if (base == null || has(cambios, 'grado')) return null;

  const rw = String(actual.grado || '').toUpperCase().includes('RW');
  let factor = 1;
  let aplicado = false;
  const aplicar = (campoActual, campoAnterior) => {
    const ratio = ratioCampo(campoActual, campoAnterior);
    if (ratio == null) return false;
    factor *= ratio;
    aplicado = true;
    return true;
  };

  if (has(cambios, 'paquetes')) aplicar(actual.paquetes, anterior.paquetes);
  if (!rw && has(cambios, 'pcs_paq')) aplicar(actual.pcs_paq, anterior.pcs_paq);

  if (ganancia) {
    const dimensionFields = rw
      ? ['ancho_ganancia', 'largo_ganancia']
      : ['espesor_ganancia', 'ancho_ganancia', 'largo_ganancia'];
    for (const field of dimensionFields) {
      if (!has(cambios, field)) continue;
      const baseField = field.replace('_ganancia', '');
      aplicar(
        actual[field] ?? actual[baseField],
        anterior[field] ?? anterior[baseField]
      );
    }
    if (rw && has(cambios, 'castigo_rw_ganancia')) {
      aplicar(
        actual.castigo_rw_ganancia ?? actual.castigo_rw,
        anterior.castigo_rw_ganancia ?? anterior.castigo_rw
      );
    }
  } else {
    const dimensionFields = rw ? ['ancho', 'largo'] : ['espesor', 'ancho', 'largo'];
    for (const field of dimensionFields) {
      if (has(cambios, field)) aplicar(actual[field], anterior[field]);
    }
    if (rw && has(cambios, 'castigo_rw')) {
      aplicar(actual.castigo_rw, anterior.castigo_rw);
    }
  }

  return aplicado ? roundN(base * factor, 3) : null;
}

/**
 * Recalcula una línea del Packing List después de aplicar cambios del formulario.
 * `cambios` identifica qué campos dispararon el cálculo y evita pisar totales manuales.
 */
export function recalcularLineaPacking(linea, cambios = {}, anterior = linea) {
  const next = { ...linea };
  const cambiaCantidad = has(cambios, 'paquetes') || has(cambios, 'pcs_paq');
  const cambiaGrado = has(cambios, 'grado');
  const esRw = String(next.grado || '').toUpperCase().includes('RW');
  // En RW el M³ depende de paquetes/dimensiones; cambiar piezas por paquete
  // no debe borrar el volumen ya calculado para esa composición.
  const cambiaCantidadM3 =
    has(cambios, 'paquetes') || (!esRw && has(cambios, 'pcs_paq'));
  const cambiaProveedor =
    cambiaCantidadM3 ||
    cambiaGrado ||
    ['castigo_rw', 'espesor', 'ancho', 'largo'].some((field) => has(cambios, field));
  const cambiaGanancia =
    cambiaCantidadM3 ||
    cambiaGrado ||
    ['espesor_ganancia', 'ancho_ganancia', 'largo_ganancia', 'castigo_rw_ganancia'].some(
      (field) => has(cambios, field)
    );

  if (cambiaCantidad || cambiaGrado) {
    next.total_pcs = calcTotalPcs(next.paquetes, next.pcs_paq, next.grado);
  }

  if (cambiaProveedor && !has(cambios, 'total_m3')) {
    const soloCambioPaquetes =
      has(cambios, 'paquetes') &&
      !has(cambios, 'pcs_paq') &&
      !cambiaGrado &&
      !['castigo_rw', 'espesor', 'ancho', 'largo'].some((field) => has(cambios, field));
    const paquetesOrigen = parseNum(next.paquetes_origen);
    const m3Original = parseNum(next.total_m3_original);
    const paquetes = parseNum(next.paquetes);

    if (soloCambioPaquetes && paquetesOrigen > 0 && m3Original != null && paquetes != null) {
      next.total_m3 = roundN((m3Original * paquetes) / paquetesOrigen, 3);
    } else {
      const totalM3 = calcTotalM3(next);
      const proporcional = m3Proporcional(anterior, next, cambios);
      next.total_m3 = totalM3 ?? proporcional;
    }
  }

  if (cambiaGanancia && !has(cambios, 'total_m3_ganancia')) {
    const soloCambioPaquetes =
      has(cambios, 'paquetes') &&
      !has(cambios, 'pcs_paq') &&
      !cambiaGrado &&
      !['espesor_ganancia', 'ancho_ganancia', 'largo_ganancia', 'castigo_rw_ganancia'].some(
        (field) => has(cambios, field)
      );
    const paquetes = parseNum(next.paquetes);
    let proporcional = m3Proporcional(anterior, next, cambios, { ganancia: true });
    if (proporcional == null && soloCambioPaquetes) {
      const paquetesAnterior = parseNum(anterior.paquetes);
      const m3gAnterior = baseM3Ganancia(anterior);
      if (paquetesAnterior > 0 && m3gAnterior != null && paquetes != null) {
        proporcional = roundN((m3gAnterior * paquetes) / paquetesAnterior, 6);
      }
    }
    const totalM3Ganancia = m3Ganancia(next);
    const usarFormula = totalM3Ganancia != null && totalM3Ganancia !== 0;
    if (esRw) {
      if (usarFormula) {
        next.total_m3_ganancia = totalM3Ganancia;
      } else if (proporcional != null) {
        next.total_m3_ganancia = proporcional;
      } else if (totalM3Ganancia != null) {
        next.total_m3_ganancia = totalM3Ganancia;
      }
    } else if (usarFormula) {
      next.total_m3_ganancia = totalM3Ganancia;
    } else if (proporcional != null) {
      next.total_m3_ganancia = proporcional;
    } else if (totalM3Ganancia != null) {
      next.total_m3_ganancia = totalM3Ganancia;
    }
  }

  if (
    cambiaProveedor ||
    has(cambios, 'total_m3') ||
    has(cambios, 'precio_usd_m3')
  ) {
    const precio = parseNum(next.precio_usd_m3);
    const totalM3 = parseNum(next.total_m3);
    next.total_usd = precio != null && totalM3 != null
      ? roundN(precio * totalM3, 2)
      : null;
  }

  return next;
}
